import {CacheMap} from '@gl/lit/lib/cache_map.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {RelevancyWeights} from '@gl/lit/utils/fuzzysearch.js';
import type {FuseResult, IFuseOptions} from 'fuse.js';
import Fuse from 'fuse.js';

export enum SortBy {
  Name,
  Org,
}

export type ImpersonationOptionData = {
  org: string;
  team: string;
  team_id: number;
  incomplete: boolean;
  role: string;
  email: Nullable<string>;
  name: Nullable<string>;
  nickname: Nullable<string>;
  id: number;
};

export type RankedImpersonationOptionData = {
  item: ImpersonationOptionData;
  refIndex: number;
  score: number;
};

export type ImpersonationOptions = ImpersonationOptionData[];

const baseFuseOptions: IFuseOptions<ImpersonationOptionData> = {
  isCaseSensitive: false,
  includeScore: true,
  shouldSort: true,
  includeMatches: true,
  findAllMatches: false,
  minMatchCharLength: 2,
  location: 0,
  threshold: 0.6,
  distance: 100,
  useExtendedSearch: false,
  ignoreLocation: false,
  ignoreFieldNorm: true,
  fieldNormWeight: 0.75,
};

const weightedSearch: Record<
  SortBy,
  IFuseOptions<ImpersonationOptionData>['keys']
> = {
  [SortBy.Org]: [
    {
      name: 'org',
      weight: RelevancyWeights.HIGHEST,
    },
    {
      name: 'email',
      weight: RelevancyWeights.MEDIUM,
      getFn: data => data.email?.split('@')[1] ?? '',
    },
  ],
  [SortBy.Name]: [
    {
      name: 'name',
      weight: RelevancyWeights.HIGHEST,
    },
    {
      name: 'nickname',
      weight: RelevancyWeights.MEDIUM,
    },
    {
      name: 'email',
      weight: RelevancyWeights.LOW,
      getFn: data => data.email?.split('@')[0] ?? '',
    },
  ],
};

const sorts: Record<
  SortBy,
  (
    a: FuseResult<ImpersonationOptionData>,
    b: FuseResult<ImpersonationOptionData>
  ) => number
> = {
  [SortBy.Org]: (a, b) => {
    if (a.score != b.score) return (a.score ?? 0) - (b.score ?? 0);
    const aOrg = a.item.org ?? '';
    const bOrg = b.item.org ?? '';
    if (aOrg != bOrg) return aOrg.localeCompare(bOrg);
    const aName = a.item.name ?? '';
    const bName = b.item.name ?? '';
    return aName.localeCompare(bName);
  },
  [SortBy.Name]: (a, b) => {
    if (a.score != b.score) return (a.score ?? 0) - (b.score ?? 0);
    const aName = a.item.name ?? '';
    const bName = b.item.name ?? '';
    if (aName != bName) return aName.localeCompare(bName);
    const aOrg = a.item.org ?? '';
    const bOrg = b.item.org ?? '';
    return aOrg.localeCompare(bOrg);
  },
};

function getOptions(sortBy: SortBy) {
  return {
    ...baseFuseOptions,
    keys: [...(weightedSearch[sortBy] ?? [])],
  };
}

export function createSearchEngine(list: ImpersonationOptionData[]) {
  const engines = {
    [SortBy.Org]: new Fuse(list, getOptions(SortBy.Org)),
    [SortBy.Name]: new Fuse(list, getOptions(SortBy.Name)),
  };

  const optionsCache = {
    [SortBy.Org]: CacheMap.create<string, RankedImpersonationOptionData[]>(
      'impersonation-options-org'
    ),
    [SortBy.Name]: CacheMap.create<string, RankedImpersonationOptionData[]>(
      'impersonation-options-name'
    ),
  };

  return {
    search: (term: string, sortBy: SortBy): RankedImpersonationOptionData[] => {
      const normalizedTerm = term.length >= 2 ? term : '';
      const cache = optionsCache[sortBy];
      const searchResult = cache.get(term).or(() => {
        const engine = engines[sortBy];
        const result = normalizedTerm
          ? (engine.search(normalizedTerm) as RankedImpersonationOptionData[])
          : list.map(
              (item, idx) =>
                ({
                  item,
                  refIndex: idx,
                  score: 0,
                }) as RankedImpersonationOptionData
            );
        return result.sort(sorts[sortBy]);
      });
      return searchResult;
    },
  };
}
